import { optimizedApiService } from '../config/api.config';

export interface Breed {
  id: number;
  name: string;
  petType: number; // 1 = cat, 2 = dog
  /** Its other names ("Cavapoo" for Cavoodle): MPG's, and this salon's own (mpgadmin D87). */
  alsoCalled?: string[];
}

/** The search box's match: the name or one of its other names. */
export const breedMatches = (breed: Breed, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [breed.name, ...(breed.alsoCalled ?? [])].some(n => n.toLowerCase().includes(q));
};

// Display ordering for the breed dropdowns. The breeds API returns rows in
// id/insertion order with no popularity field, so we order them here:
//   1. PINNED_BREEDS  — always shown first (e.g. "Mixed-breed")
//   2. POPULAR_BREEDS — breeds that most often come in for grooming, in this
//      curated order (coat type drives grooming frequency, so the oodles and
//      clip/scissor breeds rank highest; short-coat breeds that rarely book a
//      groom are intentionally left out and fall into the alphabetical list)
//   3. everything else — alphabetically
// Names must match the API's `name` exactly to be ranked.
const PINNED_BREEDS = ['Mixed-breed'];

const POPULAR_BREEDS = [
  // Dogs — by how often they come in for grooming
  // Oodles / doodles (regular full clips — the bread and butter of AU salons)
  'Cavoodle', 'Groodle', 'Labradoodle', 'Spoodle', 'Poochon', 'Schnoodle',
  // Clip / scissor breeds
  'Maltese', 'Shih Tzu', 'Poodle', 'Poodle Standard', 'Poodle Miniature', 'Poodle Toy',
  'Miniature Schnauzer', 'Bichon Frise', 'Cocker Spaniel', 'English Cocker Spaniel',
  'Cavalier King Charles Spaniel', 'Lhasa Apso', 'West Highland White Terrier',
  'Yorkshire Terrier', 'Pomeranian',
  // Heavy de-shedders
  'Golden Retriever', 'Border Collie', 'German Shepherd', 'Samoyed', 'Old English Sheepdog',
  // Cats — long-haired coats need grooming most
  'Persian', 'Domestic Longhair', 'Maine Coon', 'Ragdoll', 'Himalayan',
  'British Shorthair', 'Domestic Shorthair',
];

const breedRank = (name: string): number => {
  const pinned = PINNED_BREEDS.indexOf(name);
  if (pinned !== -1) return pinned;
  const popular = POPULAR_BREEDS.indexOf(name);
  if (popular !== -1) return PINNED_BREEDS.length + popular;
  return Number.MAX_SAFE_INTEGER;
};

const sortBreedsForDisplay = (breeds: Breed[]): Breed[] =>
  [...breeds].sort((a, b) => {
    const rankDiff = breedRank(a.name) - breedRank(b.name);
    // Same rank (both pinned/popular ties, or both "the rest") → alphabetical
    return rankDiff !== 0 ? rankDiff : a.name.localeCompare(b.name);
  });

// True for the pinned + curated breeds, so the dropdowns can show them under a
// "Popular breeds" group header (the rest go under an "All breeds" group).
export const isPopularBreed = (name: string): boolean =>
  PINNED_BREEDS.includes(name) || POPULAR_BREEDS.includes(name);

/**
 * The breed list. One read and one cache with the services step's best
 * match (`optimizedApiService.fetchBreeds`): the two used to read the same
 * list each, at the same moment, when a pet's breed name had to be looked up.
 */
export const fetchBreeds = async (): Promise<Breed[]> => {
  try {
    const breeds = await optimizedApiService.fetchBreeds();
    return Array.isArray(breeds) ? breeds : [];
  } catch (error) {
    console.error('Error fetching breeds:', error);
    throw error;
  }
};

export const getBreedsByPetType = async (petType: 'dog' | 'cat'): Promise<Breed[]> => {
  const breeds = await fetchBreeds();
  const petTypeNumber = petType === 'cat' ? 1 : 2;

  return sortBreedsForDisplay(breeds.filter(breed => breed.petType === petTypeNumber));
};

export const searchBreeds = async (query: string, petType: 'dog' | 'cat'): Promise<Breed[]> => {
  const breeds = await getBreedsByPetType(petType);
  
  if (!query.trim()) {
    return breeds;
  }
  
  return breeds.filter(breed => breedMatches(breed, query));
};

export const getBreedById = async (id: number): Promise<Breed | undefined> => {
  const breeds = await fetchBreeds();
  return breeds.find(breed => breed.id === id);
}; 